// Core
import { useCallback, useState } from 'react';
import { useMutation, useQuery } from 'react-query';

// Chakra
import {
  Text,
  useColorModeValue,
  Divider,
  Flex,
  Table,
  Thead,
  Tr,
  Td,
  Tbody,
} from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';

// Components
import NotFound from 'views/Dashboard/404.js';
import Pagination from '../../../components/Pagination';
import { useHistory } from 'react-router-dom';
import Card from '../../../components/Card/Card';
import TableSkeleton from '../../../components/TableSkeleton';
import usePermissionStatusByKey from '../../../hooks/usePermissionStatusByKey';
import { parseDateTime } from '../../../services/Utils';

const ExternalPrice = () => {
  const history = useHistory();
  const borderColor = useColorModeValue('blue.50', 'rgba(255, 255, 255, 0.10)');

  const textColor = useColorModeValue('gray.700', 'white');
  const paddingTd = '16px 0 16px 24px';
  const [offset, setOffset] = useState(0);
  const [ExternalPrices, setExternalPrices] = useState([]);
  const [externalPricesMeta, setExternalPricesMeta] = useState([]);
  const limit = 25;

  const isExternalPrices = usePermissionStatusByKey(
    'GET:/admin/external-prices',
  );

  const { isLoading: isExternalPricesLoading, isFetching, refetch } = useQuery({
    queryKey: ['external-prices', offset, isExternalPrices],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (isExternalPrices) {
        const params = {
          limit,
          offset,
        };

        const response = await apiService.getExternalPrices(params);
        setExternalPrices(response.data);
        setExternalPricesMeta(response.meta);
      }

      return true;
    },
    retry: false,
    enabled: !!isExternalPrices,
    refetchInterval: 30000,
  });

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center">
        <Text
          fontSize="40px"
          fontStyle="normal"
          fontWeight="700"
          color={textColor}
          mb="0px"
          className="page-name"
        >
          External Prices
        </Text>
      </Flex>

      <Divider
        opacity="1"
        mr="2px"
        borderColor={borderColor}
        borderTopWidth="1px"
        borderBottomWidth="0"
        mb="32px"
      />

      {isExternalPrices && (
        <>
          {isExternalPricesLoading ? (
            <TableSkeleton rowCount={5} />
          ) : ExternalPrices.length > 0 ? (
            <Card padding={'8px'}>
              <Table color={textColor}>
                <Thead>
                  <Tr>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Name
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Type
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Amount
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Last Updated
                      </Text>
                    </Td>
                  </Tr>
                </Thead>
                <Tbody>
                  {ExternalPrices.map((row) => {
                    return (
                      <Tr
                        _last={{
                          '& td': {
                            borderBottom: 'none !important',
                          },
                        }}
                        key={row.id}
                        border="unset"
                        className="cursor-tr"
                      >
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {row?.name}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {row?.type}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {row?.amount}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {parseDateTime(row?.updated)}
                          </Text>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Card>
          ) : (
            <NotFound text="Not Found" />
          )}

          <Pagination
            meta={externalPricesMeta ?? { limit, offset, total: 0 }}
            isLoadingPage={isFetching}
            currentPage={Math.floor(offset / limit) + 1}
            onPageChange={(newOffset) => setOffset(newOffset)}
          />
        </>
      )}
    </>
  );
};

export default ExternalPrice;
